import { useEffect, useRef, type RefObject } from 'react';

type UseScrollPaginatedLoadMoreOptions = {
  scrollContainerRef: RefObject<HTMLElement | null>;
  sentinelRef: RefObject<HTMLElement | null>;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  enabled?: boolean;
  /** Re-attach when list length changes so new sentinel position is observed. */
  itemCount?: number;
};

const MAX_ATTACH_ATTEMPTS = 120;

/** Infinite scroll inside a fixed-height overflow container (not the viewport). */
export function useScrollPaginatedLoadMore({
  scrollContainerRef,
  sentinelRef,
  hasMore,
  isLoading,
  isLoadingMore,
  onLoadMore,
  enabled = true,
  itemCount = 0,
}: UseScrollPaginatedLoadMoreOptions): void {
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!enabled || !hasMore || isLoading || isLoadingMore) {
      return;
    }

    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    let rafId = 0;

    const tryAttach = (attempt = 0) => {
      if (cancelled) return;

      const root = scrollContainerRef.current;
      const sentinel = sentinelRef.current;
      if (!root || !sentinel) {
        if (attempt < MAX_ATTACH_ATTEMPTS) {
          rafId = requestAnimationFrame(() => tryAttach(attempt + 1));
        }
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            onLoadMoreRef.current();
          }
        },
        { root, rootMargin: '64px', threshold: 0 }
      );

      observer.observe(sentinel);
    };

    tryAttach();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, [
    enabled,
    scrollContainerRef,
    sentinelRef,
    hasMore,
    isLoading,
    isLoadingMore,
    itemCount,
  ]);
}
